import { Heading, Subheading } from "../../../../design/heading";
import { Button } from "../../../../design/button";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { useState, useMemo, useEffect } from "react";
import { Task } from "../../../../models/Task";
import { Input } from "../../../../design/input";
import DiscoveryRequestSubtaskListItem from "./DiscoveryRequestSubtaskListItem";
import { useSubtask } from "../../../../contexts/SubtaskContext";
import { CheckBadgeIcon } from "@heroicons/react/20/solid";

interface DiscoveryRequestTaskListItemProps {
  task: Task;
}

const DiscoveryRequestTaskListItem: React.FC<
  DiscoveryRequestTaskListItemProps
> = ({ task }) => {
  const { createSubtask } = useSubtask();
  const [newSubtaskTitle, setNewSubtaskTitle] = useState("");

  // LocalStorage key based on task ID
  const localStorageKey = `task-expanded-${task._id}`;

  // Initialize expanded state from localStorage
  const [expanded, setExpanded] = useState<boolean>(() => {
    const stored = localStorage.getItem(localStorageKey);
    return stored ? JSON.parse(stored) : true;
  });

  // Update localStorage when expanded changes
  useEffect(() => {
    localStorage.setItem(localStorageKey, JSON.stringify(expanded));
  }, [expanded, localStorageKey]);

  const { completedCount, incompleteCount } = useMemo(() => {
    let completed = 0;
    let incomplete = 0;

    (task.subtasks ?? []).forEach((subtask) => {
      if (subtask.status === "complete") {
        completed++;
      } else {
        incomplete++;
      }
    });

    return { completedCount: completed, incompleteCount: incomplete };
  }, [task.subtasks]);

  const handleAddSubtask = async () => {
    if (!newSubtaskTitle.trim()) return;
    try {
      await createSubtask({
        title: newSubtaskTitle,
        task: task._id,
        status: "pending",
      });
    } catch (error) {
      console.error("Error creating subtask:", error);
    }

    setNewSubtaskTitle("");
  };

  return (
    <div className="p-4 bg-white border rounded-lg shadow-sm">
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <div className="flex items-center gap-2">
          {expanded ? (
            <ChevronDownIcon className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRightIcon className="w-4 h-4 text-gray-500" />
          )}
          <div>
            <div className="flex items-center gap-2">
              <Heading className="text-lg font-semibold">
                {task.title || "Untitled Task"}
              </Heading>
              {task.isCompleted && (
                <CheckBadgeIcon className="inline-block w-4 h-4 text-green-500" />
              )}
            </div>
            {task.description && <Subheading>{task.description}</Subheading>}
          </div>
        </div>
        {!expanded && (
          <span className="text-sm text-gray-500">
            Completed {completedCount} of {completedCount + incompleteCount}{" "}
            tasks
          </span>
        )}
      </div>

      {expanded && (
        <>
          {(task.subtasks ?? []).length > 0 ? (
            <ul className="mt-4 space-y-3">
              {(task.subtasks ?? []).map((subtask) => (
                <DiscoveryRequestSubtaskListItem
                  subtask={subtask}
                  key={subtask._id}
                />
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-gray-500">No subtasks.</p>
          )}

          {/* Subtask Input */}
          <div className="flex items-center gap-2 mt-4">
            <Input
              type="text"
              placeholder="New subtask title"
              value={newSubtaskTitle}
              onChange={(e) => setNewSubtaskTitle(e.target.value)}
              className="flex-1 px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Button
              onClick={handleAddSubtask}
              className="flex items-center gap-1"
            >
              <PlusIcon className="w-4 h-4" />
              Add
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default DiscoveryRequestTaskListItem;
